import { useEffect, useCallback, useRef, useState } from 'react';
import { saveDraft, loadDraft, clearDraft, isDraftAvailable, getDraftAge, formatDraftAge } from '../utils/draftStorage';

const useAutoSave = (leadId, autoSaveInterval = 2000) => {
  const [draftInfo, setDraftInfo] = useState({
    hasDraft: false,
    lastSaved: null,
    age: null,
    showRestoreDialog: false
  });
  
  const dataRef = useRef({});
  const timeoutRef = useRef(null);
  const isInitialized = useRef(false);
  const [hasPromptedForDraft, setHasPromptedForDraft] = useState(false);

  // Update draft info
  const updateDraftInfo = useCallback(() => {
    const hasDraft = isDraftAvailable(leadId);
    const age = getDraftAge(leadId);
    
    setDraftInfo(prev => ({
      ...prev,
      hasDraft,
      lastSaved: age ? new Date(Date.now() - (age * 60 * 60 * 1000)) : null,
      age: age ? formatDraftAge(age) : null
    }));
  }, [leadId]);

  // Save draft function
  const saveCurrentDraft = useCallback(() => {
    if (Object.keys(dataRef.current).length > 0) {
      const success = saveDraft(leadId, dataRef.current);
      if (success) {
        updateDraftInfo();
      }
      return success;
    }
    return false;
  }, [leadId, updateDraftInfo]);

  // Load draft function
  const loadCurrentDraft = useCallback(() => {
    const draft = loadDraft(leadId);
    if (draft) {
      updateDraftInfo();
      return draft.data;
    }
    return null;
  }, [leadId, updateDraftInfo]);

  // Clear draft function
  const clearCurrentDraft = useCallback(() => {
    const success = clearDraft(leadId);
    if (success) {
      dataRef.current = {};
      updateDraftInfo();
    }
    return success;
  }, [leadId, updateDraftInfo]);

  // Update data to be saved
  const updateDraftData = useCallback((newData) => {
    dataRef.current = { ...dataRef.current, ...newData };
    
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set new timeout for auto-save
    timeoutRef.current = setTimeout(() => {
      saveCurrentDraft();
    }, autoSaveInterval);
  }, [saveCurrentDraft, autoSaveInterval]);

  // Show restore dialog function
  const showRestoreDialog = useCallback(() => {
    setDraftInfo(prev => ({ ...prev, showRestoreDialog: true }));
  }, []);

  // Hide restore dialog function
  const hideRestoreDialog = useCallback(() => {
    setDraftInfo(prev => ({ ...prev, showRestoreDialog: false }));
  }, []);

  // Initialize draft info on mount and check for existing drafts
  useEffect(() => {
    if (!isInitialized.current) {
      updateDraftInfo();
      isInitialized.current = true;
      
      // Check if there's an existing draft and show restore dialog
      if (isDraftAvailable(leadId) && !hasPromptedForDraft) {
        setHasPromptedForDraft(true);
        showRestoreDialog();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]); // Intentionally limited dependencies to prevent infinite loops

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Save draft before page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        saveCurrentDraft();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saveCurrentDraft]);

  return {
    draftInfo,
    updateDraftData,
    loadDraft: loadCurrentDraft,
    clearDraft: clearCurrentDraft,
    saveDraft: saveCurrentDraft,
    showRestoreDialog,
    hideRestoreDialog
  };
};

export default useAutoSave;